import { withTranslation } from "next-i18next";
import { analytics } from "@lib/trackers/analytics";
import { useReferralProgram } from "@hooks/use-features";
import { usePrice } from "@hooks/use-prices";
import { useCurrentUser } from "@hooks/use-current-user";
import Button from "components/core/Button";
import redeemTypes from "@utils/constants/redeemTypes";

function ReferralShareLink({ trackingName, size = "normal", t }) {
  const { referree_coupon: referreeCoupon } = useReferralProgram();
  const user = useCurrentUser();
  const price = usePrice();

  const referringToken = user?.referring_token;
  const referralLink = t("common:referral.share_link_url", { code: referringToken });

  const durationInMonths = parseInt(referreeCoupon.duration_in_months, 10) || 1;
  const referreeBenefitAmount = price(referreeCoupon.value, {
    withoutCents: true,
    withoutSpace: true,
  });

  function referralLinkShareEvent() {
    analytics.track("Referral link shared", {
      userId: user?.id,
      referringToken,
      eventType: "link shared",
    });
  }

  async function shareMessage() {
    const welcomeMessage = user?.first_name
      ? t("common:referral.welcome_message_with_name", { name: user.first_name })
      : t("common:referral.welcome_message_without_name");

    const sharedataText = () => {
      switch (referreeCoupon.duration) {
        case "forever":
          switch (referreeCoupon.type) {
            case redeemTypes.couponPercentOff:
              return t("common:referral.sharedata_text.percent_off_forever", {
                welcomeMessage,
                referralLink,
                percent: referreeCoupon.value,
              });
            case redeemTypes.couponAmountOff:
              return t("common:referral.sharedata_text.amount_off_forever", {
                welcomeMessage,
                referralLink,
                amount: referreeBenefitAmount,
              });
          }
          break;
        case "once":
          switch (referreeCoupon.type) {
            case redeemTypes.couponPercentOff:
              return t("common:referral.sharedata_text.percent_off", {
                welcomeMessage,
                referralLink,
                percent: referreeCoupon.value,
                count: durationInMonths,
              });
            case redeemTypes.couponAmountOff:
              return t("common:referral.sharedata_text.amount_off", {
                welcomeMessage,
                referralLink,
                amount: referreeBenefitAmount,
                count: durationInMonths,
              });
          }
      }
    };

    const shareData = {
      title: t("common:referral.sharedata_title", {
        discount:
          referreeCoupon.type === redeemTypes.couponPercentOff
            ? `${referreeCoupon.value}%`
            : referreeBenefitAmount,
      }),
      text: sharedataText(),
      url: referralLink,
    };

    if (navigator.canShare(shareData)) {
      await navigator.share(shareData);

      referralLinkShareEvent();
    }
  }

  return (
    <Button
      trackingName={trackingName}
      onClick={shareMessage}
      size={size}
      hugContent
    >
      {t("common:referral.share_link_btn")}
    </Button>
  );
}

export default withTranslation("common")(ReferralShareLink);
